import { _js } from '@ifixit/localize';
import { WikiTextEditor } from 'Shared/wiki_text_editor';
import { Modal } from 'Shared/modal';
import { Utils } from 'Shared/utils';
import BulletSelector from 'Guide/bullet_selector';

///////////////////////////////////////////////////////////////////////////////
// TREE LINE CLASS
//

/**
 * A TreeLine is an Editable, but with a lot of added behavior specific to one
 * line of a step; TreeLines interact heavily with the TreeEditor class.
 */
let TreeLine = new Class({
   Implements: [Options, Events],

   options: {
      level: 0,
      bullet: 'black',
      source: _js('New line.'),
      allowEmpty: false,
      maxLength: 350,
      errorHandler: Utils.ignoreApplicationErrors,
   },

   // Aggregated objects.
   tree: null, // TreeEditor this line belongs to.
   bulletSelector: null, // BulletSelector that handles changing bullet.

   // Data.
   lineid: null, // Int identifier from the DB.
   bullet: null, // String holding bullet type.
   level: null, // Int giving indent level.
   source: null, // String holding the current source.
   lastSource: null, // String holding the last iteration of the source.
   originalSource: null, // String holding the source on page load.
   editing: null, // Whether this line's being edited.
   modified: null, // Whether this line is modified from page load.
   fresh: null, // Whether this line is newly-made.

   // Callbacks.
   errorHandler: null, // Function called when an error is raised.

   // DOM elements.
   lineEl: null, // Holds the whole line.
   textEl: null, // Holds the rendered text.
   bulletEl: null, // For the bullet.
   bulletIconEl: null, // For the bullet icon.
   editorEl: null, // Wrapper for edit mode.
   inputEl: null, // Text input for edit mode.
   charCounterEl: null, // Displays character count in edit mode.
   toolbarEl: null, // Toolbar in edit mode (below)
   wikiToolbarEl: null, // Toolbar in edit mode (above)

   /**
    * A new TreeLine, which may be initialized empty in order to create a new
    * line, or from the DOM node 'line', in order to add behavior to a line
    * already existing on the page.
    */
   initialize: function (tree, id, lineEl, options) {
      this.tree = tree;
      this.setOptions(options);

      let groups, bulletEl;

      // Are we using a preexisting line, or creating one from scratch?
      if (lineEl) {
         // Use the existing line.
         this.lineEl = lineEl;
         this.lineid = this.lineEl.getProperty('data-lineid');
         this.fresh = false;

         // Get the text element, store its source text, and give it an id.
         this.textEl = this.lineEl.getElement('p');
         this.source = this.textEl.getProperty('data-wikitext');
         this.textEl.setAttribute('id', 'line' + id);

         // Figure out our level from the line className.
         groups = this.lineEl.className.match(/\blevel(\d+)\b/);
         this.level = Number(groups[1]);

         // Figure out our bullet type. First try looking for a bullet icon
         // (which identifies things like cautions, notes, and reminders where
         // the bullet doesn't).
         bulletEl = this.lineEl.getElement('.bulletIcon');
         if (bulletEl) {
            // There was a bullet icon, so we store a reference to it.
            this.bulletIconEl = bulletEl;
            // We also store a reference to the normal bullet icon.
            this.bulletEl = this.lineEl.getElement('.bullet');
         } else {
            // There's no bullet icon, but we still make it and hide it, to be
            // used if the user adds one later.
            this.bulletIconEl = new Element('div').hide();
            this.bulletIconEl.inject(this.textEl, 'before');
            // There will always be a bullet element, which we fall back to.
            bulletEl = this.bulletEl = this.lineEl.getElement('.bullet');
         }
         // Look for a className starting with bullet_, followed by any number
         // of underscore-separated words, and take the last one (e.g.
         // bullet_icon_caution => icon_caution, bullet_red => red).
         groups = bulletEl.className.match(/\bbullet_((?:[a-z]+_)*[a-z]+)\b/);
         this.bullet = groups[1];
      } else {
         // Create our own line and text.
         this.lineEl = new Element('div')
            .adopt(
               (this.bulletEl = new Element('div')),
               (this.bulletIconEl = new Element('div')),
               (this.textEl = new Element('p', {
                  id: 'line' + id,
                  text: this.options.source,
               })),
               new Element('div').addClass('clearer')
            )
            .setProperty('class', 'line level' + this.level);
         this.fresh = true;
         this.level = this.options.level;
         // Set the bullet from the options, assuming there is no bullet icon
         // (null); this will set the right bullet type, hide the bulletIconEl
         // as necessary, and set the right class on bulletEl.
         this.setBullet(this.options.bullet, null);
         // Set the source.
         this.source = this.options.source;
      }

      // Add hover events to the text element.
      this.textEl.addEvents({
         mouseenter: this.activate.bind(this),
         mouseleave: this.deactivate.bind(this),
      });

      // Click events have to go through the editor manager.
      this.requestEditMode = this.requestEditMode.bind(this);
      this.textEl.addEvent('click', this.requestEditMode);

      // An event handler that ends edit mode when the mouse is clicked
      // anywhere outside of the editor.
      this.clickedOff = function (ev) {
         this.tree.manager.endEditMode();
      }.bind(this);

      this.lineText = this.textEl.getParent('.lineText') || this.lineEl;

      this.lineText.addEvent('clickoff', () => {
         if (!Modal.isOpen()) {
            this.clickedOff();
         }
      });

      // Build the toolbar that sits under the text in edit mode.
      this.toolbarEl = new Element('div').addClass('toolbar');

      // Build the buttons in the toolbar.
      [
         { className: 'shiftLeft', action: 'shiftLineLeft', icon: 'arrow-left' },
         { className: 'shiftRight', action: 'shiftLineRight', icon: 'arrow-right' },
         { className: 'addLine', action: 'insertLineBelow', icon: 'plus' },
         { className: 'deleteLine', action: 'deleteLine', icon: 'times' },
      ].forEach(function (btn) {
         let div = new Element('div').addClass(btn.className);
         let iconHtml = new Element('i', { class: 'fa fa-' + btn.icon });
         iconHtml.inject(div);
         div.addEvents({
            mouseenter: div.addClass.bind(div, btn.className + 'Active'),
            mouseleave: div.removeClass.bind(div, btn.className + 'Active'),
            click: function (ev) {
               ev.stop();
               try {
                  this.tree[btn.action](this);
               } catch (e) {
                  this.errorHandler(e);
               }
            }.bind(this),
         }).inject(this.toolbarEl);
      }, this);

      // Inject the toolbar into the line, but don't show it.
      this.toolbarEl.hide().inject(this.lineEl);

      // Create a bullet selector and replace our bullet element with it.
      this.bulletSelector = new BulletSelector(this.bullet);
      this.bulletEl.dispose();
      this.bulletSelector.inject(this.lineEl);

      // Watch for the bullet selection event.
      this.bulletSelector.addEvents({
         onActivate: function () {
            this.setDirty(true);
            this.tree.manager.endEditMode();
         }.bind(this),
         onBulletSelected: function (bullet, icon) {
            this.setBullet(bullet, icon);
            // If we're editing and the textarea is in the DOM, re-focus it.
            when(this.lineEl.getElement('textarea'), textarea => {
               textarea.focus();
            });
            this.tree.updateModified();
         }.bind(this),
      });

      // The editor, which contains a text area and a wrapper for styling.
      this.editorEl = new Element('div').addClass('editor').adopt(
         new Element('div').addClass('textWrapper').adopt(
            (this.inputEl = new Element('textarea', {
               events: {
                  keydown: this.editorKeydown.bind(this),
                  mouseenter: this.tree.blurLines.bind(this.tree),
                  keyup: this.update.bind(this),
                  paste: this.update.bind(this),
               },
            }))
         )
      );

      // Build the options available in the wiki text editor toolbar
      let items = ['bold', 'italic', 'underline', 'link'];

      // Build the toolbar that sits over the text in edit mode.
      let wikiToolbar = new WikiTextEditor(this.inputEl, null, {
         toolbarItems: items,
         className: 'wikiTextEditorToolbar mini' + (App.hideIcon ? ' noIcon' : ''),
         helpLink: window.guide_constants.GuideURI('TAG_FORMATTING_HELP'),
         helpText: App.hideIcon ? _js('Formatting help') : '',
         defaultOpenInNewTab: true,
      });
      this.wikiToolbarEl = wikiToolbar.toolbar.addClass('inGuideStep');
      this.lineEl.grab(this.wikiToolbarEl.hide(), 'top');

      // The character counter, which warns the user when he's entered too many
      // characters into the field.
      this.charCounterEl = new Element('p', {
         html: '<span class="count">0</span>/' + this.options.maxLength,
      })
         .setProperty('class', 'charCounter')
         .inject(this.editorEl);

      this.editing = false;
      this.modified = false;
      this.originalSource = this.lastSource = this.source;
      this.errorHandler = this.options.errorHandler;
   },

   activate: function (ev) {
      this.textEl.addClass('active');
   },

   deactivate: function (ev) {
      if (!this.editing) {
         this.textEl.removeClass('active');
      }
   },

   /**
    * Provides several keyboard shortcuts; all shortcuts can use 'ctrl' or
    * 'alt' interchangeably:
    *
    *    escape: render current
    *    ctrl+n: render current, move to next
    *    ctrl+p: render current, move to previous
    *    ctrl+shift+n: render current, insert and move to new line below
    *    ctrl+shift+p: render current, insert and move to new line above
    *    ctrl+]: indent current
    *    ctrl+[: dedent current
    *    ctrl+d: delete current
    */
   editorKeydown: function (ev) {
      try {
         // move to previous/next, inserting if we're at the top/bottom
         if (ev.key == 'enter') {
            ev.stop();
            if (ev.shift) {
               this.tree.editPreviousOrInsertAbove(this);
            } else {
               this.tree.editNextOrInsertBelow(this);
            }
         }

         // shift left/right
         if (ev.key == 'tab') {
            ev.stop();
            if (ev.shift) {
               this.tree.shiftLineLeft(this);
            } else {
               this.tree.shiftLineRight(this);
            }
         }

         // Escape exits edit mode.
         if (ev.key == 'esc') {
            ev.stop();
            this.tree.manager.endEditMode();
         }

         // all of the CTRL-like commands
         if (ev.control || ev.alt) {
            if (ev.key == 'n') {
               // edit/insert line below
               ev.stop();
               if (ev.shift) {
                  this.tree.insertLineBelow(this);
               } else {
                  this.tree.editNextLine(this);
               }
            } else if (ev.key == 'p') {
               // edit/insert line above
               ev.stop();
               if (ev.shift) {
                  this.tree.insertLineAbove(this);
               } else {
                  this.tree.editPreviousLine(this);
               }
            } else if (ev.key == 'd') {
               // delete line
               ev.stop();
               this.tree.deleteLine(this);
            }
         }
      } catch (e) {
         this.errorHandler(e);
      }

      // Call update in case the user entered an actual character.
      this.update();
   },

   /**
    * Updates the source on each change to allow modification checks while the
    * user is editing a field.
    */
   update: function () {
      // Don't allow just whitespace to pass as text.
      this.source = this.inputEl.value.trim();
      this.modified = this.source != this.originalSource;
      // See if we're over the max length.
      this.checkLength();
      if (this.modified) {
         this.tree.updateModified();
      }
   },

   /**
    * Get length without any wikisyntax.
    */
   getAdjustedTextLength: function (text) {
      return text.replace(/\[.*?]/g, '').length;
   },

   /**
    * Updates the character count element with the new count, and adds (or
    * removes) the class 'overMaxCharCount' to the charCounter elements if the
    * count is over (or under) the maximum.
    */
   checkLength: function () {
      let text = this.inputEl.value;
      let length = this.getAdjustedTextLength(text);

      let saveBtn = $('saveStep');
      this.charCounterEl.getElement('.count').set('html', length);
      if (length > this.options.maxLength) {
         this.charCounterEl.addClass('overMaxCharCount');
         saveBtn.disable();
      } else {
         this.charCounterEl.removeClass('overMaxCharCount');
         saveBtn.enable();
      }
   },

   inject: function (otherLine, where) {
      this.lineEl.inject(otherLine.lineEl, where);
   },

   dispose: function () {
      this.lineEl.dispose();
   },

   // ACCESSORS ///////////////////////////////////////////////////////////////

   /**
    * Returns true if a user has never (ever, even before the page load)
    * modified this line.
    */
   isFresh: function () {
      return this.fresh && !this.modified;
   },

   /**
    * Returns true if this line has no text.
    */
   isEmpty: function () {
      return this.source == '';
   },

   /**
    * Gives a string representation of the line; used to determine if the line
    * has been modified from its original state.
    */
   serialize: function () {
      return this.level + this.bullet + this.source;
   },

   // MUTATORS ////////////////////////////////////////////////////////////////

   /**
    * Sets the level for this line, and updates the display to match it.
    */
   setLevel: function (level) {
      this.lineEl.removeClass('level' + this.level);
      this.lineEl.addClass('level' + level);
      this.level = level;
   },

   /**
    * Sets the bullet type for this line and updates the display to match it,
    * which includes setting the appropriate classes on the bulletEl and
    * bulletIconEl.
    */
   setBullet: function (bullet, icon) {
      //null bug here
      this.bulletEl.className = 'fa fa-circle bullet bullet_' + bullet;

      if (icon) {
         this.bulletIconEl.className = 'bullet bulletIcon ico-step-' + icon.replace('_', '-');
         this.bulletIconEl.show();
         this.bulletEl.hide();
      } else {
         this.bulletIconEl.hide();
         this.bulletEl.show();
      }
      // Store the icon name (e.g. caution) if it's present, otherwise use the
      // bullet name, which will be a color.
      this.bullet = icon ? icon : bullet;
   },

   /**
    * Allows artificial tampering with the fresh state, to let the tree editor
    * pretend that this line is or isn't fresh, preventing it from being
    * deleted when left.
    */
   setDirty: function (dirty) {
      this.fresh = !dirty;
   },

   // EDIT MODE TRANSITIONS ///////////////////////////////////////////////////

   requestEditMode: function (ev) {
      this.tree.manager.beginEditMode(this.tree, this);
   },

   beginEditMode: function () {
      // Don't do anything if we're already in edit mode.
      if (this.editing) {
         return;
      }

      this.editing = true;

      // Update the input and check the content length to see if it's over the
      // maximum, even before the user types anything.
      this.inputEl.value = this.lastSource = this.source;
      this.checkLength();

      // Remove the bullet icon, replace the rendered node with the editor, and
      // select the input text.
      this.bulletIconEl.dispose();
      this.editorEl.replaces(this.textEl);
      this.inputEl.activate();

      // Periodically check to see if the text has changed.
      this.updateCheck = this.update.periodical(1000, this);

      // Display the toolbar and set field to be active.
      this.wikiToolbarEl.show();
      this.toolbarEl.show();
      this.activate();

      // Disable the click event.
      this.textEl.removeEvent('click', this.requestEditMode);
   },

   endEditMode: function () {
      // Don't do anything if we're not in edit mode.
      if (!this.editing) {
         return;
      }

      if (this.textEl.get('text') !== this.source) {
         this.lineEl.removeClass('newLine');
      }

      // Get the new text.
      let source = this.inputEl.value;

      // Check for empty text.
      if (!this.options.allowEmpty && source === '') {
         throw new ApplicationError(_js('You must enter some text.'));
      }

      // Get length of text - length of wiki syntax.
      let length = this.getAdjustedTextLength(source);

      // Disallow long text.
      if (length > this.options.maxLength) {
         // Acount for length of wiki syntax.
         throw new ApplicationError(_js('You cannot enter more than 350 characters per bullet.'));
      }

      // Get rendered text via AJAX if the source has changed.
      if (this.lastSource != source) {
         this.source = source;
         this.textEl.addClass('updating');
         this.textEl.set('text', source);
         this.modified = this.source !== this.originalSource;
         new Request.AjaxIO('getRendered', {
            onSuccess: function (rendered) {
               this.textEl.set('html', rendered);
               this.textEl.removeClass('updating');
            }.bind(this),
         }).send(source);
      }

      // Restore to view mode.
      this.toolbarEl.hide();
      this.wikiToolbarEl.hide();
      this.textEl.replaces(this.editorEl);
      this.bulletIconEl.inject(this.textEl, 'before');

      // Stop checking to see if the text has changed.
      clearInterval(this.updateCheck);

      // Re-enable the click event.
      this.textEl.addEvent('click', this.requestEditMode);

      // Deactivation only works when the `editing` flag isn't set, so we need
      // to unset `editing` before deactivating.
      this.editing = false;
      this.deactivate();
   },
});

export default TreeLine;
